/* eslint-disable */
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import axios from 'axios';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://server-godday-backend.onrender.com/';

// Configure axios defaults
axios.defaults.baseURL = API_BASE_URL;
axios.defaults.withCredentials = true;

// Auth Context
const AuthContext = createContext();

// Action Types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  OTP_REQUIRED: 'OTP_REQUIRED',
  OTP_VERIFY_START: 'OTP_VERIFY_START',
  OTP_VERIFY_SUCCESS: 'OTP_VERIFY_SUCCESS',
  OTP_VERIFY_FAILURE: 'OTP_VERIFY_FAILURE',
  SIGNUP_START: 'SIGNUP_START',
  SIGNUP_SUCCESS: 'SIGNUP_SUCCESS',
  SIGNUP_FAILURE: 'SIGNUP_FAILURE',
  SIGNUP_OTP_REQUIRED: 'SIGNUP_OTP_REQUIRED',
  SIGNUP_OTP_VERIFY_START: 'SIGNUP_OTP_VERIFY_START',
  SIGNUP_OTP_VERIFY_SUCCESS: 'SIGNUP_OTP_VERIFY_SUCCESS',
  SIGNUP_OTP_VERIFY_FAILURE: 'SIGNUP_OTP_VERIFY_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_USER: 'SET_USER',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Initial State
const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  isLoading: true,
  error: null,
  otpRequired: false,
  loginEmail: null,
  signupOtpRequired: false,
  signupEmail: null,
};

// Auth Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null,
        otpRequired: false,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      const { token, user } = action.payload;
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      return {
        ...state,
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        otpRequired: false,
        loginEmail: null,
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
        otpRequired: false,
        loginEmail: null,
      };

    case AUTH_ACTIONS.OTP_REQUIRED:
      return {
        ...state,
        otpRequired: true,
        loginEmail: action.payload.email,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.OTP_VERIFY_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AUTH_ACTIONS.OTP_VERIFY_SUCCESS:
      return {
        ...state,
        ...action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        otpRequired: false,
        loginEmail: null,
      };

    case AUTH_ACTIONS.OTP_VERIFY_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };

    case AUTH_ACTIONS.SIGNUP_START:
      return {
        ...state,
        isLoading: true,
        error: null,
        signupOtpRequired: false,
      };

    case AUTH_ACTIONS.SIGNUP_SUCCESS:
      const { token: signupToken, user: signupUser } = action.payload;
      localStorage.setItem('token', signupToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${signupToken}`;
      return {
        ...state,
        user: signupUser,
        token: signupToken,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        signupOtpRequired: false,
        signupEmail: null,
      };

    case AUTH_ACTIONS.SIGNUP_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
        signupOtpRequired: false,
        signupEmail: null,
      };

    case AUTH_ACTIONS.SIGNUP_OTP_REQUIRED:
      return {
        ...state,
        signupOtpRequired: true,
        signupEmail: action.payload.email,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.SIGNUP_OTP_VERIFY_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AUTH_ACTIONS.SIGNUP_OTP_VERIFY_SUCCESS:
      return {
        ...state,
        ...action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        signupOtpRequired: false,
        signupEmail: null,
      };

    case AUTH_ACTIONS.SIGNUP_OTP_VERIFY_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };

    case AUTH_ACTIONS.LOGOUT:
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        otpRequired: false,
        loginEmail: null,
        signupOtpRequired: false,
        signupEmail: null,
      };

    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false,
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Only logout on 401 if it's an auth-related endpoint
        // Don't logout for folder password protection or file access issues
        if (error.response?.status === 401) {
          const url = error.config?.url || '';
          const isAuthEndpoint = url.includes('/api/auth/') || url.includes('/api/auth/verify');
          const isProtectedFolderError = error.response?.data?.protected === true;

          // Only logout if it's an auth endpoint failure, not a folder password issue
          if (isAuthEndpoint && !isProtectedFolderError) {
            console.log('🔐 Auth token invalid, logging out');
            dispatch({ type: AUTH_ACTIONS.LOGOUT });
          } else {
            console.log('🔒 Folder access denied, but keeping user logged in');
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('token');

      if (!token) {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        return;
      }

      try {
        const response = await axios.get('/api/auth/verify');
        dispatch({
          type: AUTH_ACTIONS.SET_USER,
          payload: response.data.user,
        });
      } catch (error) {
        console.error('Auth verification failed:', error);
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email, password) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const response = await axios.post('/api/auth/login', { email, password });

      // Direct login success
      if (response.data.success && response.data.token) {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            token: response.data.token,
            user: response.data.user
          },
        });
        return { success: true, requiresOtp: false, data: response.data };
      }

      // Fallback if backend still sends OTP (though it shouldn't)
      // After successful login, require OTP verification
      dispatch({
        type: AUTH_ACTIONS.OTP_REQUIRED,
        payload: { email },
      });

      return { success: true, requiresOtp: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Login failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  const verifyOtp = async (email, otp) => {
    dispatch({ type: AUTH_ACTIONS.OTP_VERIFY_START });

    try {
      const response = await axios.post('/api/auth/verify-otp', { email, otp });
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      dispatch({
        type: AUTH_ACTIONS.OTP_VERIFY_SUCCESS,
        payload: { token, user },
      });

      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'OTP verification failed';
      dispatch({
        type: AUTH_ACTIONS.OTP_VERIFY_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  const verifySignupOtp = async (email, otp) => {
    dispatch({ type: AUTH_ACTIONS.SIGNUP_OTP_VERIFY_START });

    try {
      const response = await axios.post('/api/auth/verify-signup-otp', { email, otp });
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      dispatch({
        type: AUTH_ACTIONS.SIGNUP_OTP_VERIFY_SUCCESS,
        payload: { token, user },
      });

      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Signup OTP verification failed';
      dispatch({
        type: AUTH_ACTIONS.SIGNUP_OTP_VERIFY_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  const signup = async (signupData) => {
    dispatch({ type: AUTH_ACTIONS.SIGNUP_START });

    try {
      const { name, email, password, location } = signupData;
      const username = email.split('@')[0]; // Generate username from email

      const response = await axios.post('/api/auth/signup', {
        username,
        email,
        password,
        name,
        location
      });

      // After successful signup, require OTP verification
      dispatch({
        type: AUTH_ACTIONS.SIGNUP_OTP_REQUIRED,
        payload: { email },
      });

      return { success: true, requiresOtp: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Signup failed';
      dispatch({
        type: AUTH_ACTIONS.SIGNUP_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  const updateUser = (userData) => {
    dispatch({
      type: AUTH_ACTIONS.SET_USER,
      payload: { ...state.user, ...userData },
    });
  };

  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Helper function to check if token is still valid
  const isTokenValid = () => {
    const token = localStorage.getItem('token');
    if (!token) return false;

    try {
      // Basic token validation (you might want to add JWT decode here)
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) return false;

      // If we have a user in state and token exists, assume valid
      return !!state.user;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  };

  const value = {
    user: state.user,
    token: state.token,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    otpRequired: state.otpRequired,
    loginEmail: state.loginEmail,
    signupOtpRequired: state.signupOtpRequired,
    signupEmail: state.signupEmail,
    login,
    verifyOtp,
    signup,
    verifySignupOtp,
    logout,
    updateUser,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    window.location.href = '/';
    return null;
  }

  return children;
};

export default AuthContext;
